import React, { useRef, useState } from 'react';
import ToolLayout from 'tools/ToolLayout';
import {
  btnRow,
  card,
  chipStyle,
  dangerBtn,
  errorBox,
  formatMb,
  hint,
  label,
  primaryBtn,
  privacy,
  secondaryBtn,
  warnBox,
} from 'tools/toolStyles';
import {
  downloadBlob,
  ensureFFmpeg,
  extFromName,
  getWin,
  hasSharedArrayBuffer,
  replaceExt,
  revokeUrl,
  runFFmpegJob,
} from 'tools/ffmpegShared';

type AudioFmt = 'mp3' | 'm4a' | 'ogg' | 'opus' | 'wav';

const WARN_MB = 50;

const FORMATS: { id: AudioFmt; label: string; ext: string; mime: string; args: (inName: string, outName: string) => string[] }[] = [
  {
    id: 'mp3',
    label: 'MP3',
    ext: 'mp3',
    mime: 'audio/mpeg',
    args: (i, o) => ['-i', i, '-c:a', 'libmp3lame', '-b:a', '192k', o],
  },
  {
    id: 'm4a',
    label: 'M4A (AAC)',
    ext: 'm4a',
    mime: 'audio/mp4',
    args: (i, o) => ['-i', i, '-c:a', 'aac', '-b:a', '160k', o],
  },
  {
    id: 'ogg',
    label: 'OGG',
    ext: 'ogg',
    mime: 'audio/ogg',
    args: (i, o) => ['-i', i, '-c:a', 'libvorbis', '-q:a', '4', o],
  },
  {
    id: 'opus',
    label: 'OPUS',
    ext: 'opus',
    mime: 'audio/opus',
    args: (i, o) => ['-i', i, '-c:a', 'libopus', '-b:a', '128k', o],
  },
  {
    id: 'wav',
    label: 'WAV',
    ext: 'wav',
    mime: 'audio/wav',
    args: (i, o) => ['-i', i, '-c:a', 'pcm_s16le', o],
  },
];

const ConvertAudio = () => {
  const abortRef = useRef({ aborted: false });
  const [file, setFile] = useState<any>(null);
  const [fmt, setFmt] = useState<AudioFmt>('mp3');
  const [status, setStatus] = useState('');
  const [progress, setProgress] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [outUrl, setOutUrl] = useState<string | null>(null);
  const [outBlob, setOutBlob] = useState<any>(null);
  const [outName, setOutName] = useState('');

  const sab = hasSharedArrayBuffer();
  const win = getWin();

  const onPick = (e: any) => {
    const f = e.target && e.target.files && e.target.files[0];
    if (!f) return;
    setFile(f);
    setError('');
    revokeUrl(outUrl);
    setOutUrl(null);
    setOutBlob(null);
  };

  const cancel = () => {
    abortRef.current.aborted = true;
    setStatus('Đang hủy…');
  };

  const run = async () => {
    if (!file) {
      setError('Hãy chọn file âm thanh.');
      return;
    }
    const spec = FORMATS.find((f) => f.id === fmt)!;
    abortRef.current = { aborted: false };
    setBusy(true);
    setProgress(0);
    setError('');
    revokeUrl(outUrl);
    setOutUrl(null);
    setOutBlob(null);
    try {
      await ensureFFmpeg(setStatus);
      setStatus('Đang chuyển đổi…');
      const inExt = extFromName(file.name, 'mp3');
      const inName = `input.${inExt}`;
      const outFile = `output.${spec.ext}`;
      const blob = await runFFmpegJob(
        file,
        inName,
        outFile,
        spec.args(inName, outFile),
        spec.mime,
        {
          onProgress: setProgress,
          signal: abortRef.current,
          onStatus: setStatus,
        }
      );
      if (abortRef.current.aborted) {
        setStatus('Đã hủy.');
        return;
      }
      const name = replaceExt(file.name, spec.ext);
      const url = win.URL.createObjectURL(blob);
      setOutBlob(blob);
      setOutUrl(url);
      setOutName(name);
      setProgress(1);
      setStatus('Xong — tải file bên dưới.');
    } catch (err: any) {
      if (abortRef.current.aborted) setStatus('Đã hủy.');
      else {
        setError((err && err.message) || 'Chuyển đổi thất bại.');
        setStatus('');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <ToolLayout title="Đổi đuôi âm thanh">
      <p style={privacy}>
        Âm thanh được xử lý hoàn toàn trên máy bạn (FFmpeg.wasm), không gửi lên server language123.net.
      </p>

      {!sab ? (
        <p style={warnBox}>
          Trang chưa bật SharedArrayBuffer. Hãy mở trực tiếp{' '}
          <a href="/tools/convert-audio">/tools/convert-audio</a> (tải lại trang đầy đủ).
        </p>
      ) : null}

      <div style={card}>
        <label style={label}>1. Chọn file âm thanh</label>
        <p style={hint}>MP3 / WAV / OGG / M4A / FLAC · gợi ý ≤ ~{WARN_MB} MB.</p>
        <input type="file" accept="audio/*" onChange={onPick} />
        {file ? (
          <p style={{ ...hint, marginTop: 8, marginBottom: 0 }}>
            {file.name} · {formatMb(file.size)} MB
          </p>
        ) : null}
        {file && file.size > WARN_MB * 1024 * 1024 ? (
          <p style={{ ...warnBox, marginTop: 12 }}>File lớn — có thể chậm hoặc treo tab.</p>
        ) : null}
      </div>

      <div style={card}>
        <label style={label}>2. Định dạng xuất</label>
        <div style={btnRow}>
          {FORMATS.map((f) => (
            <button key={f.id} type="button" style={chipStyle(fmt === f.id)} onClick={() => setFmt(f.id)}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div style={card}>
        <div style={btnRow}>
          <button type="button" style={secondaryBtn} disabled={busy || !sab} onClick={() => ensureFFmpeg(setStatus).catch((e: any) => setError(e.message))}>
            Tải FFmpeg trước
          </button>
          <button type="button" style={primaryBtn} disabled={!file || busy || !sab} onClick={run}>
            {busy ? `Đang chuyển… ${Math.round(progress * 100)}%` : 'Chuyển đổi'}
          </button>
          {busy ? (
            <button type="button" style={dangerBtn} onClick={cancel}>
              Hủy
            </button>
          ) : null}
          {outBlob ? (
            <button type="button" style={primaryBtn} onClick={() => downloadBlob(outBlob, outName)}>
              Tải về
            </button>
          ) : null}
        </div>
        {busy || progress > 0 ? (
          <div style={{ height: 8, background: '#eceff1', borderRadius: 4, overflow: 'hidden', marginBottom: 8 }}>
            <div style={{ width: `${Math.round(progress * 100)}%`, height: '100%', background: '#1976d2' }} />
          </div>
        ) : null}
        {status ? <p style={hint}>{status}</p> : null}
        {error ? <p style={errorBox}>{error}</p> : null}
        {outUrl ? <audio src={outUrl} controls style={{ width: '100%', marginTop: 8 }} /> : null}
      </div>
    </ToolLayout>
  );
};

export default ConvertAudio;
